import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { useQuota } from '../../context/QuotaContext';
import { profilesService, leadsService, projectsService, templatesService, calendarService, paymentsService, supabaseData } from '../../services/supabase';
import { exportToJSON } from '../../utils/exportUtils';
import Card, { CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import { PAGE_STACK, PAGE_GRID_GAP } from '../../styles/pageLayout';
import { navigateToPricing } from '../../utils/navigationUtils';
import Input from '../../components/ui/Input';
import InstallPWAButton from '../../components/common/InstallPWAButton';
import EmailSignatureEditor from '../../components/settings/EmailSignatureEditor';
import { sanitizeEmailSignatureHtml, htmlToPlainText, getEmailSignatureSizeError, prepareEmailSignatureHtml, EMAIL_SIGNATURE_MAX_HTML_CHARS, EMAIL_SIGNATURE_MAX_PLAIN_CHARS } from '../../utils/emailSignatureHtml';
import UsageBar from '../../components/ui/UsageBar';
import { billingService } from '../../services/billing';
import DeleteAccountModal from '../../components/settings/DeleteAccountModal';
import IntegrationCard, {
  IntegrationCardStatus,
} from '../../components/settings/IntegrationCard';
import UpgradePromptModal from '../../components/ui/UpgradePromptModal';
import { ENABLE_SUPER_PDP } from '../../config/featureFlags';
import { redirectToExternal } from '../../utils/pwa';
import {
  markRedirectedToPortal,
  isReturningFromPortal,
  hasPortalRedirectFlag,
  clearPortalState,
} from '../../utils/checkoutStorage';
import { 
  User as UserIcon, 
  Save,
  CreditCard,
  Download,
  Settings as SettingsIcon,
  Trash2,
  Shield,
  Check,
  Sparkles,
  SlidersHorizontal,
  Puzzle,
  FileText,
} from 'lucide-react';
import { US, FR } from 'country-flag-icons/react/3x2';
import { useAppearance } from '../../context/AppearanceContext';
import type { AccentId } from '../../styles/accentPresets';
import {
  CALENDAR_COLOR_PALETTE,
  CALENDAR_EVENT_TYPE_IDS,
  CALENDAR_EVENT_TYPE_I18N_KEYS,
} from '../../styles/calendarEventTypeColors';
import { navigationColors, alertBox, highlightRing } from '../../styles/colors';
import { googleCalendarService, GoogleCalendarStatus } from '../../services/googleCalendar';
import { googleGmailService, GoogleGmailStatus } from '../../services/googleGmail';
import { googleDriveService, GoogleDriveStatus } from '../../services/googleDrive';
import {
  formatSiretDisplay,
  normalizeSiretInput,
  sirenFromSiret,
  isValidSiret,
} from '../../utils/siret';
import {
  normalizeCountryCode,
  normalizePostalCode,
  normalizeVatNumber,
} from '../../utils/billingIdentity';

interface UserFormData {
  name: string;
  company: string;
  taxRate: number;
  siret: string;
  vatNumber: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  countryCode: string;
}

const emptyToNull = (value: string | undefined | null): string | null => {
  const trimmed = (value || '').trim();
  return trimmed ? trimmed : null;
};

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: authUser, updateProfile, signOut } = useAuth();
  const authUserId = authUser?.id;
  const { language, setLanguage, t } = useI18n();
  const { showToast } = useToast();
  const { accentId, setAccentId, presets, eventTypeColors, setEventTypeColor } = useAppearance();
  const { usage, usageLoading, refreshUsage } = useQuota();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState<any>(null);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [isExportingData, setIsExportingData] = useState(false);
  const [exportCooldownSeconds, setExportCooldownSeconds] = useState<number | null>(null);
  const [isDeleteAccountOpen, setIsDeleteAccountOpen] = useState(false);
  const [gcalStatus, setGcalStatus] = useState<GoogleCalendarStatus | null>(null);
  const [gcalLoading, setGcalLoading] = useState(false);
  const [gcalBusy, setGcalBusy] = useState(false);
  const [gcalLoadError, setGcalLoadError] = useState(false);
  const [gmailStatus, setGmailStatus] = useState<GoogleGmailStatus | null>(null);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailBusy, setGmailBusy] = useState(false);
  const [gdriveStatus, setGdriveStatus] = useState<GoogleDriveStatus | null>(null);
  const [gdriveLoading, setGdriveLoading] = useState(false);
  const [gdriveBusy, setGdriveBusy] = useState(false);
  const [showSuperPdpProModal, setShowSuperPdpProModal] = useState(false);
  const [emailSignature, setEmailSignature] = useState('');
  const [emailSignatureHtml, setEmailSignatureHtml] = useState('');
  const [followUpReminderOnDueDay, setFollowUpReminderOnDueDay] = useState(true);
  const [followUpReminderReady, setFollowUpReminderReady] = useState(false);
  const [highlightEmailSignature, setHighlightEmailSignature] = useState(false);
  const [highlightTaxRate, setHighlightTaxRate] = useState(false);
  const emailSignatureReadyRef = useRef(false);
  const emailSignatureLastSavedRef = useRef({ plain: '', html: '' });
  const emailSignatureSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSignatureFocusRef = useRef(false);
  const wasManagingSubscriptionRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const gcalCallbackHandledRef = useRef(false);
  const gmailCallbackHandledRef = useRef(false);
  const gdriveCallbackHandledRef = useRef(false);

  // Load profile data from Supabase
  useEffect(() => {
    const loadProfile = async () => {
      if (authUser) {
        emailSignatureReadyRef.current = false;
        setFollowUpReminderReady(false);
        const profile = await profilesService.getProfile();
        setProfileData(profile);
        const plain =
          typeof profile?.email_signature === 'string' ? profile.email_signature : '';
        const rawHtml =
          typeof profile?.email_signature_html === 'string' ? profile.email_signature_html : '';
        const html = rawHtml ? await prepareEmailSignatureHtml(rawHtml) : '';
        setEmailSignature(plain);
        setEmailSignatureHtml(html);
        emailSignatureLastSavedRef.current = { plain, html };
        emailSignatureReadyRef.current = true;
        const prefs =
          profile?.preferences && typeof profile.preferences === 'object' && !Array.isArray(profile.preferences)
            ? (profile.preferences as Record<string, unknown>)
            : {};
        setFollowUpReminderOnDueDay(prefs.followUpReminderOnDueDay !== false);
        setFollowUpReminderReady(true);
      }
    };
    loadProfile();
  }, [authUser]);

  // Autosave email signature (debounced) — no explicit Save button
  useEffect(() => {
    if (!emailSignatureReadyRef.current || !authUser) return;

    const plain = emailSignature.trimEnd();
    const html = emailSignatureHtml.trim()
      ? sanitizeEmailSignatureHtml(emailSignatureHtml)
      : '';
    const derivedPlain = plain || (html ? htmlToPlainText(html) : '');
    const last = emailSignatureLastSavedRef.current;
    if (last.plain === derivedPlain && last.html === html) return;

    if (emailSignatureSaveTimerRef.current) {
      clearTimeout(emailSignatureSaveTimerRef.current);
    }

    emailSignatureSaveTimerRef.current = setTimeout(() => {
      void (async () => {
        const normalizedHtml = html ? await prepareEmailSignatureHtml(html) : '';
        const derivedPlainNormalized = plain || (normalizedHtml ? htmlToPlainText(normalizedHtml) : '');
        if (normalizedHtml !== html) {
          setEmailSignatureHtml(normalizedHtml);
          if (!plain.trim() && derivedPlainNormalized) {
            setEmailSignature(derivedPlainNormalized);
          }
        }

        const sizeError = getEmailSignatureSizeError({
          html: normalizedHtml,
          plain: derivedPlainNormalized,
        });
        if (sizeError === 'html_too_large') {
          showToast(
            t('settings.emailSignatureTooLargeHtml', {
              max: EMAIL_SIGNATURE_MAX_HTML_CHARS.toLocaleString(),
            }),
            'error'
          );
          return;
        }
        if (sizeError === 'plain_too_large') {
          showToast(
            t('settings.emailSignatureTooLargePlain', {
              max: EMAIL_SIGNATURE_MAX_PLAIN_CHARS.toLocaleString(),
            }),
            'error'
          );
          return;
        }

        try {
          const updated = await profilesService.updateProfile({
            email_signature: derivedPlainNormalized || null,
            email_signature_html: normalizedHtml || null,
          });
          if (!updated) {
            showToast(t('settings.emailSignatureSaveError'), 'error');
            return;
          }
          setProfileData(updated);
          emailSignatureLastSavedRef.current = {
            plain: derivedPlainNormalized,
            html: normalizedHtml,
          };
        } catch {
          showToast(t('settings.emailSignatureSaveError'), 'error');
        }
      })();
    }, 700);

    return () => {
      if (emailSignatureSaveTimerRef.current) {
        clearTimeout(emailSignatureSaveTimerRef.current);
      }
    };
  }, [authUser, emailSignature, emailSignatureHtml, showToast, t]);

  useEffect(() => {
    return () => {
      if (emailSignatureSaveTimerRef.current) clearTimeout(emailSignatureSaveTimerRef.current);
    };
  }, []);

  // Check export cooldown on mount
  useEffect(() => {
    if (!authUser) return;
    
    const checkCooldown = () => {
      const lastExportKey = `leadsme:export:lastExport:${authUser.id}`;
      const lastExportTime = localStorage.getItem(lastExportKey);
      
      if (lastExportTime) {
        const timeSinceLastExport = Date.now() - parseInt(lastExportTime, 10);
        const cooldownMs = 5 * 60 * 1000; // 5 minutes
        const remainingMs = cooldownMs - timeSinceLastExport;
        
        if (remainingMs > 0) {
          setExportCooldownSeconds(Math.ceil(remainingMs / 1000));
        } else {
          setExportCooldownSeconds(null);
        }
      } else {
        setExportCooldownSeconds(null);
      }
    };
    
    checkCooldown();
    const interval = setInterval(checkCooldown, 1000);
    
    return () => clearInterval(interval);
  }, [authUser]);

  // CRITICAL: Check for return from Stripe billing portal IMMEDIATELY (synchronously)
  // useLayoutEffect runs synchronously before browser paint, catching browser back button early
  // Standard SaaS: centralized checkoutStorage helpers (flag + timestamp)
  useLayoutEffect(() => {
    if (!hasPortalRedirectFlag()) return;

    const returned = isReturningFromPortal();
    clearPortalState();

    if (returned) {
      setManagingSubscription(false);
      wasManagingSubscriptionRef.current = false;

      const targetTab = 'subscription';
      setActiveTab(targetTab);

      const currentTabParam = searchParams.get('tab');
      const urlParams = new URLSearchParams(window.location.search);
      const urlTabParam = urlParams.get('tab');
      if (currentTabParam !== targetTab || urlTabParam !== targetTab) {
        navigate(`/app/settings?tab=${targetTab}`, { replace: true });
      }

      if (authUser) {
        void refreshUsage({ force: true });
      }
    }
  }, [authUser, navigate, searchParams, refreshUsage]);

  // Sync activeTab with URL param (runs after useLayoutEffect)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (!tabParam) return;

    // Legacy tab aliases → current tabs
    const legacyMap: Record<string, string> = {
      usage: 'subscription',
      financial: 'profile',
      language: 'preferences',
      appearance: 'preferences',
      app: 'account',
    };
    const normalizedTab = legacyMap[tabParam] || tabParam;
    const validTabs = ['profile', 'preferences', 'integrations', 'subscription', 'account'];

    if (!validTabs.includes(normalizedTab)) return;

    if (normalizedTab !== activeTab) {
      setActiveTab(normalizedTab);
    }
    if (tabParam !== normalizedTab) {
      navigate(`/app/settings?tab=${normalizedTab}`, { replace: true });
    }
  }, [searchParams, activeTab, navigate]);

  // Gmail status (needed on Preferences for signature visibility, not only Integrations)
  useEffect(() => {
    if (!authUserId) {
      setGmailStatus(null);
      return;
    }
    let cancelled = false;
    setGmailStatus(null);
    setGmailLoading(true);
    googleGmailService
      .getStatus()
      .then((status) => {
        if (!cancelled) setGmailStatus(status);
      })
      .catch(() => {
        if (!cancelled) setGmailStatus({ connected: false, configured: true });
      })
      .finally(() => {
        if (!cancelled) setGmailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authUserId]);

  // Google Calendar connection status + OAuth return (?gcal=connected|error)
  useEffect(() => {
    if (activeTab !== 'integrations') return;
    let cancelled = false;
    setGcalStatus(null);
    setGcalLoading(true);
    setGcalLoadError(false);
    googleCalendarService
      .getStatus()
      .then((status) => {
        if (!cancelled) setGcalStatus(status);
      })
      .catch(() => {
        // Don't treat network/auth errors as "not configured" — secrets may be fine
        if (!cancelled) {
          setGcalStatus(null);
          setGcalLoadError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setGcalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, authUserId]);

  // Google Drive connection status
  useEffect(() => {
    if (activeTab !== 'integrations') return;
    let cancelled = false;
    setGdriveStatus(null);
    setGdriveLoading(true);
    googleDriveService
      .getStatus()
      .then((status) => {
        if (!cancelled) setGdriveStatus(status);
      })
      .catch(() => {
        if (!cancelled) setGdriveStatus({ connected: false, configured: true });
      })
      .finally(() => {
        if (!cancelled) setGdriveLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, authUserId]);

  useEffect(() => {
    const gcal = searchParams.get('gcal');
    if (!gcal || gcalCallbackHandledRef.current) return;

    // Land on integrations after OAuth return or deep-link connect
    setActiveTab('integrations');

    const clearParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('gcal');
      if (!next.get('tab')) next.set('tab', 'integrations');
      navigate(`/app/settings?${next.toString()}`, { replace: true });
    };

    if (gcal === 'connect') {
      gcalCallbackHandledRef.current = true;
      clearParam();
      void (async () => {
        try {
          setGcalBusy(true);
          const url = await googleCalendarService.getConnectUrl();
          window.location.assign(url);
        } catch {
          setGcalBusy(false);
          showToast(t('settings.googleCalendarErrorToast'), 'error');
        }
      })();
      return;
    }

    if (gcal !== 'connected' && gcal !== 'error') return;
    gcalCallbackHandledRef.current = true;

    if (gcal === 'connected') {
      showToast(t('settings.googleCalendarConnectedToast'), 'success');
      setGcalBusy(true);
      googleCalendarService
        .syncAll()
        .then(() => googleCalendarService.getStatus())
        .then((status) => setGcalStatus(status))
        .catch(() => {
          /* sync-all best-effort */
        })
        .finally(() => setGcalBusy(false));
    } else {
      showToast(t('settings.googleCalendarErrorToast'), 'error');
    }
    clearParam();
  }, [searchParams, navigate, showToast, t]);

  // Gmail OAuth return (?gmail=connected|error|connect)
  useEffect(() => {
    const gmail = searchParams.get('gmail');
    if (!gmail || gmailCallbackHandledRef.current) return;

    const clearParam = (tab: string) => {
      const next = new URLSearchParams(searchParams);
      next.delete('gmail');
      next.set('tab', tab);
      navigate(`/app/settings?${next.toString()}`, { replace: true });
    };

    if (gmail === 'connect') {
      gmailCallbackHandledRef.current = true;
      setActiveTab('integrations');
      clearParam('integrations');
      void (async () => {
        try {
          setGmailBusy(true);
          const url = await googleGmailService.getConnectUrl();
          window.location.assign(url);
        } catch {
          setGmailBusy(false);
          showToast(t('settings.googleGmailErrorToast'), 'error');
        }
      })();
      return;
    }

    if (gmail !== 'connected' && gmail !== 'error') return;
    gmailCallbackHandledRef.current = true;

    if (gmail === 'connected') {
      // Land on Preferences immediately (no Integrations flash). Optimistic connected
      // so the signature block mounts; if status disagrees, fall back to Integrations.
      pendingSignatureFocusRef.current = true;
      setGmailStatus((prev) => prev ?? { connected: true, configured: true });
      setActiveTab('preferences');
      clearParam('preferences');
      void googleGmailService
        .getStatus()
        .then((status) => {
          setGmailStatus(status);
          if (!status.connected) {
            pendingSignatureFocusRef.current = false;
            setHighlightEmailSignature(false);
            setActiveTab('integrations');
            clearParam('integrations');
            showToast(t('settings.googleGmailErrorToast'), 'error');
            return;
          }
          showToast(t('settings.googleGmailConnectedSignatureToast'), 'success');
        })
        .catch(() => {
          // OAuth callback already persisted tokens; keep optimistic connected.
          setGmailStatus({ connected: true, configured: true });
          showToast(t('settings.googleGmailConnectedSignatureToast'), 'success');
        });
      return;
    }

    // OAuth failed: stay on Integrations so the user can retry Connect.
    pendingSignatureFocusRef.current = false;
    setActiveTab('integrations');
    showToast(t('settings.googleGmailErrorToast'), 'error');
    clearParam('integrations');
  }, [searchParams, navigate, showToast, t]);

  // Google Drive OAuth return (?gdrive=connected|error|connect)
  useEffect(() => {
    const gdrive = searchParams.get('gdrive');
    if (!gdrive || gdriveCallbackHandledRef.current) return;

    const clearParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('gdrive');
      next.set('tab', 'integrations');
      navigate(`/app/settings?${next.toString()}`, { replace: true });
    };

    if (gdrive === 'connect') {
      gdriveCallbackHandledRef.current = true;
      setActiveTab('integrations');
      clearParam();
      void (async () => {
        try {
          setGdriveBusy(true);
          const url = await googleDriveService.getConnectUrl();
          window.location.assign(url);
        } catch {
          setGdriveBusy(false);
          showToast(t('settings.googleDriveErrorToast'), 'error');
        }
      })();
      return;
    }

    if (gdrive !== 'connected' && gdrive !== 'error') return;
    gdriveCallbackHandledRef.current = true;
    setActiveTab('integrations');

    if (gdrive === 'connected') {
      setGdriveStatus((prev) => prev ?? { connected: true, configured: true });
      clearParam();
      void googleDriveService
        .getStatus()
        .then((status) => {
          setGdriveStatus(status);
          if (!status.connected) {
            showToast(t('settings.googleDriveErrorToast'), 'error');
            return;
          }
          showToast(t('settings.googleDriveConnectedToast'), 'success');
        })
        .catch(() => {
          setGdriveStatus({ connected: true, configured: true });
          showToast(t('settings.googleDriveConnectedToast'), 'success');
        });
      return;
    }

    showToast(t('settings.googleDriveErrorToast'), 'error');
    clearParam();
  }, [searchParams, navigate, showToast, t]);

  // After Gmail connect: scroll + soft highlight on signature (app standard ring)
  useEffect(() => {
    if (!pendingSignatureFocusRef.current) return;
    if (activeTab !== 'preferences') return;
    if (!gmailStatus?.connected) return;

    let cancelled = false;
    let attempts = 0;

    const tryFocus = () => {
      if (cancelled) return;
      const el =
        document.getElementById('settings-email-signature-field') ||
        document.getElementById('settings-email-signature');
      if (!el) {
        if (attempts < 20) {
          attempts += 1;
          window.setTimeout(tryFocus, 50);
        }
        return;
      }
      pendingSignatureFocusRef.current = false;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightEmailSignature(true);
    };

    const timer = window.setTimeout(tryFocus, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeTab, gmailStatus?.connected]);

  useEffect(() => {
    if (!highlightEmailSignature) return;
    const timer = window.setTimeout(() => setHighlightEmailSignature(false), 1500);
    return () => window.clearTimeout(timer);
  }, [highlightEmailSignature]);

  // From Finances: ?focus=taxRate → scroll + highlight (keep param until applied)
  useEffect(() => {
    if (searchParams.get('focus') !== 'taxRate') return;

    if (activeTab !== 'profile') {
      setActiveTab('profile');
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'profile');
      navigate(`/app/settings?${next.toString()}`, { replace: true });
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const tryFocus = () => {
      if (cancelled) return;
      const el = document.getElementById('settings-tax-rate');
      if (!el) {
        if (attempts < 40) {
          attempts += 1;
          window.setTimeout(tryFocus, 50);
        }
        return;
      }

      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightTaxRate(true);

      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      next.set('tab', 'profile');
      navigate(`/app/settings?${next.toString()}`, { replace: true });

      if (el instanceof HTMLInputElement) {
        window.setTimeout(() => {
          if (!cancelled) el.focus({ preventScroll: true });
        }, 200);
      }
    };

    const timer = window.setTimeout(tryFocus, 100);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchParams, activeTab, navigate]);

  useEffect(() => {
    if (!highlightTaxRate) return;
    const timer = window.setTimeout(() => setHighlightTaxRate(false), 1500);
    return () => window.clearTimeout(timer);
  }, [highlightTaxRate]);

  const handleGcalConnect = async () => {
    try {
      setGcalBusy(true);
      const url = await googleCalendarService.getConnectUrl();
      window.location.assign(url);
    } catch {
      setGcalBusy(false);
      showToast(t('settings.googleCalendarErrorToast'), 'error');
    }
  };

  // Browser back from Google OAuth can restore bfcache with gcalBusy=true — unlock the button
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setGcalBusy(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  useEffect(() => {
    if (activeTab === 'integrations' && !searchParams.get('gcal')) {
      setGcalBusy(false);
    }
  }, [activeTab, searchParams]);

  const handleGcalDisconnect = async () => {
    try {
      setGcalBusy(true);
      await googleCalendarService.disconnect();
      setGcalStatus((prev) =>
        prev ? { ...prev, connected: false, email: null, calendarId: null } : { connected: false, configured: true }
      );
      showToast(t('settings.googleCalendarDisconnectedToast'), 'success');
    } catch {
      showToast(t('settings.googleCalendarErrorToast'), 'error');
    } finally {
      setGcalBusy(false);
    }
  };

  const handleGmailConnect = async () => {
    try {
      setGmailBusy(true);
      const url = await googleGmailService.getConnectUrl();
      window.location.assign(url);
    } catch {
      setGmailBusy(false);
      showToast(t('settings.googleGmailErrorToast'), 'error');
    }
  };

  const handleGmailDisconnect = async () => {
    try {
      setGmailBusy(true);
      await googleGmailService.disconnect();
      setGmailStatus((prev) =>
        prev ? { ...prev, connected: false, email: null } : { connected: false, configured: true }
      );
      showToast(t('settings.googleGmailDisconnectedToast'), 'success');
    } catch {
      showToast(t('settings.googleGmailErrorToast'), 'error');
    } finally {
      setGmailBusy(false);
    }
  };

  const handleGdriveConnect = async () => {
    try {
      setGdriveBusy(true);
      const url = await googleDriveService.getConnectUrl();
      window.location.assign(url);
    } catch {
      setGdriveBusy(false);
      showToast(t('settings.googleDriveErrorToast'), 'error');
    }
  };

  const handleGdriveDisconnect = async () => {
    try {
      setGdriveBusy(true);
      await googleDriveService.disconnect();
      setGdriveStatus((prev) =>
        prev ? { ...prev, connected: false, email: null } : { connected: false, configured: true }
      );
      showToast(t('settings.googleDriveDisconnectedToast'), 'success');
    } catch {
      showToast(t('settings.googleDriveErrorToast'), 'error');
    } finally {
      setGdriveBusy(false);
    }
  };

  const handleSuperPdpConnect = () => {
    if (usage?.plan?.name === 'pro') {
      showToast(t('settings.superPdpComingSoonToast'), 'info');
      return;
    }
    setShowSuperPdpProModal(true);
  };

  const handleManageSubscription = async () => {
    try {
      setManagingSubscription(true);
      wasManagingSubscriptionRef.current = true;

      // CRITICAL: Set flag IMMEDIATELY before any async operations
      markRedirectedToPortal();

      const { url } = await billingService.createPortalSession();
      redirectToExternal(url, {
        openInNewWindow: false,
        fallbackUrl: '/app/settings?tab=subscription'
      });
    } catch (error) {
      console.error('Error creating portal session:', error);
      clearPortalState();
      setManagingSubscription(false);
      wasManagingSubscriptionRef.current = false;
      showToast(t('settings.portalError') || 'Erreur lors de l\'ouverture du portail de gestion. Veuillez réessayer.', 'error');
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<UserFormData>({
    defaultValues: {
      name: authUser?.user_metadata?.name || '',
      company: '',
      taxRate: 25, // will be replaced by DB value on load
      siret: '',
      vatNumber: '',
      addressLine1: '',
      addressLine2: '',
      postalCode: '',
      city: '',
      countryCode: 'FR',
    }
  });

  const siretValue = watch('siret') || '';
  const derivedSiren = sirenFromSiret(siretValue);

  // Sync form with profile data from DB on initial load only
  useEffect(() => {
    if (profileData && isInitialLoadRef.current) {
      reset({
        name: profileData?.name || authUser?.user_metadata?.name || '',
        company: profileData?.company || '',
        taxRate: ((profileData?.tax_rate ?? 0.25) * 100),
        siret: formatSiretDisplay(profileData?.siret || ''),
        vatNumber: profileData?.vat_number || '',
        addressLine1: profileData?.address_line1 || '',
        addressLine2: profileData?.address_line2 || '',
        postalCode: profileData?.postal_code || '',
        city: profileData?.city || '',
        countryCode: profileData?.country_code || 'FR',
      });
      isInitialLoadRef.current = false;
    }
  }, [profileData, authUser, reset]);

  const onSubmit = async (data: UserFormData) => {
    setIsLoading(true);
    
    try {
      // Update profile in Supabase Auth (name only)
      const { error: profileError } = await updateProfile({
        name: data.name,
      });

      if (profileError) {
        console.error('Error updating profile in Supabase:', profileError);
        throw new Error(t('settings.profileUpdateError'));
      }

      const siretDigits = normalizeSiretInput(data.siret || '');
      // DB accepts null/empty or exactly 14 digits — incomplete values are cleared, no UX validation.
      const siretForDb = siretDigits.length === 14 ? siretDigits : null;
      const vat = normalizeVatNumber(data.vatNumber || '');
      const country = normalizeCountryCode(data.countryCode || '') || 'FR';

      const billingPayload = {
        name: data.name,
        company: data.company,
        tax_rate: data.taxRate / 100,
        siret: siretForDb,
        vat_number: vat || null,
        address_line1: emptyToNull(data.addressLine1),
        address_line2: emptyToNull(data.addressLine2),
        postal_code: emptyToNull(normalizePostalCode(data.postalCode || '')),
        city: emptyToNull(data.city),
        country_code: country,
      };

      // Settings always updates an existing row. Never fall back to POST/create —
      // that produced prod 400 pairs (failed PATCH → duplicate POST) and false success toasts.
      const updatedProfile = await profilesService.updateProfile(billingPayload);
      if (!updatedProfile) {
        throw new Error(t('settings.profileUpdateError'));
      }

      setProfileData(updatedProfile);
      showToast(t('settings.profileUpdateSuccess'), 'success');
    } catch (error) {
      console.error('Error updating user:', error);
      showToast(t('settings.profileUpdateError'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportAllData = async () => {
    if (!authUser) {
      showToast(t('common.notAuthenticated') || 'Vous devez être connecté pour exporter vos données.', 'error');
      return;
    }

    // Check cooldown
    if (exportCooldownSeconds !== null && exportCooldownSeconds > 0) {
      const minutes = Math.floor(exportCooldownSeconds / 60);
      const seconds = exportCooldownSeconds % 60;
      showToast(
        t('settings.exportCooldown', { minutes, seconds }) || 
        `Veuillez patienter ${minutes}min ${seconds}s avant de réexporter vos données.`,
        'warning'
      );
      return;
    }

    setIsExportingData(true);
    try {
      // Fetch all user data in parallel
      const [allLeads, allProjects, allTemplates, allEvents, allPayments, userProfile] = await Promise.all([
        leadsService.getAll(),
        projectsService.getAll(),
        templatesService.getAll(),
        calendarService.getAll(),
        paymentsService.getAll(),
        profilesService.getProfile(),
      ]);

      // Get files metadata (if needed - just metadata, not the files themselves)
      // Note: We don't export actual file content, just metadata for GDPR compliance
      const filesMetadata: any[] = [];
      try {
        const { data: filesData } = await supabaseData
          .from('files')
          .select('id, file_name, mime_type, size_bytes, created_at, entity_type, entity_id')
          .eq('owner_user_id', authUser.id)
          .eq('status', 'active');
        if (filesData) {
          filesMetadata.push(...filesData);
        }
      } catch (error) {
        console.error('Error fetching files metadata:', error);
        // Continue without files metadata
      }

      // Structure the export data
      const exportData = {
        metadata: {
          exportDate: new Date().toISOString(),
          version: '1.0',
          userId: authUser.id,
          userEmail: authUser.email,
          description: t('settings.dataExportDescription') || 'Export complet de toutes vos données Leadsme (conforme RGPD)',
        },
        profile: userProfile || null,
        leads: allLeads || [],
        projects: allProjects || [],
        templates: allTemplates || [],
        calendar: allEvents || [],
        finances: allPayments || [],
        files: filesMetadata || [],
        summary: {
          totalLeads: allLeads?.length || 0,
          totalProjects: allProjects?.length || 0,
          totalTemplates: allTemplates?.length || 0,
          totalEvents: allEvents?.length || 0,
          totalPayments: allPayments?.length || 0,
          totalFiles: filesMetadata?.length || 0,
        },
      };

      exportToJSON(exportData, `leadsme-export-${new Date().toISOString().split('T')[0]}`);
      
      // Store last export time and start cooldown
      const lastExportKey = `leadsme:export:lastExport:${authUser.id}`;
      localStorage.setItem(lastExportKey, Date.now().toString());
      setExportCooldownSeconds(5 * 60); // 5 minutes
      
      showToast(t('settings.dataExportSuccess') || 'Export réussi ! Téléchargement en cours...', 'success');
    } catch (error) {
      console.error('Error exporting all data:', error);
      showToast(t('settings.dataExportError') || 'Erreur lors de l\'export de vos données', 'error');
    } finally {
      setIsExportingData(false);
    }
  };

  const tabs = [
    { id: 'profile', label: t('settings.profile'), icon: UserIcon },
    { id: 'preferences', label: t('settings.preferences'), icon: SlidersHorizontal },
    { id: 'integrations', label: t('settings.integrations'), icon: Puzzle },
    { id: 'subscription', label: t('settings.subscription'), icon: CreditCard },
    { id: 'account', label: t('settings.account'), icon: Shield },
  ];

  const gcalCardStatus: IntegrationCardStatus = gcalLoading
    ? 'loading'
    : gcalLoadError
      ? 'error'
      : gcalStatus && gcalStatus.configured === false
        ? 'unavailable'
        : gcalStatus?.connected
          ? 'connected'
          : 'disconnected';

  const gmailCardStatus: IntegrationCardStatus = gmailLoading
    ? 'loading'
    : gmailStatus && gmailStatus.configured === false
      ? 'unavailable'
      : gmailStatus?.connected
        ? 'connected'
        : 'disconnected';

  const gdriveCardStatus: IntegrationCardStatus = gdriveLoading
    ? 'loading'
    : gdriveStatus && gdriveStatus.configured === false
      ? 'unavailable'
      : gdriveStatus?.connected
        ? 'connected'
        : 'disconnected';

  const integrationsConnectedCount = useMemo(() => {
    let count = 0;
    if (gcalCardStatus === 'connected') count += 1;
    if (gmailCardStatus === 'connected') count += 1;
    if (gdriveCardStatus === 'connected') count += 1;
    // Super PDP not connectable yet
    return count;
  }, [gcalCardStatus, gmailCardStatus, gdriveCardStatus]);

  const handleAccentChange = (id: AccentId) => {
    if (id === accentId) return;
    setAccentId(id);
  };

  return (
    <div className={`w-full ${PAGE_STACK}`}>
      {/* Header */}
      <PageHeader
        title={t('settings.title')}
        description={t('settings.subtitle')}
      />

      <div className={`grid grid-cols-1 lg:grid-cols-4 ${PAGE_GRID_GAP}`}>
        {/* Navigation */}
        <div className="lg:col-span-1">
          <Card padding="sm">
            <nav className="space-y-1.5">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    navigate(`/app/settings?tab=${tab.id}`, { replace: true });
                  }}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-xl group ${
                    activeTab === tab.id
                      ? `${navigationColors.settingsTab.active.background} ${navigationColors.settingsTab.active.text} ${navigationColors.settingsTab.active.border} ${navigationColors.settingsTab.active.shadow}`
                      : 'text-slate-600 hover:bg-gray-50 hover:text-slate-900 transition-colors duration-150'
                  }`}
                >
                  <div className={`p-2 rounded-lg mr-3 ${
                    activeTab === tab.id
                      ? `${navigationColors.settingsTab.active.iconBackground} ${navigationColors.settingsTab.active.iconText} shadow-sm`
                      : 'bg-gray-100 text-slate-500 group-hover:bg-gray-200 group-hover:text-slate-600 transition-colors duration-150'
                  }`}>
                    <tab.icon className="w-4 h-4" />
                  </div>
                  <span className="flex-1 text-left">{tab.label}</span>
                  {activeTab === tab.id && (
                    <div className={`w-2 h-2 ${navigationColors.settingsTab.active.indicator} rounded-full shadow-sm`}></div>
                  )}
                </button>
              ))}
            </nav>
          </Card>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {activeTab === 'profile' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.profile')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">{t('settings.identity')}</h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                      label={t('settings.name')}
                      {...register('name', { required: t('settings.nameRequired') })}
                      error={errors.name?.message}
                    />

                    <Input
                      label={t('settings.company')}
                      {...register('company')}
                      error={errors.company?.message}
                      placeholder={t('settings.companyPlaceholder')}
                    />
                    </div>
                  </div>

                  <div className="space-y-4 border-t border-gray-100 pt-6">
                    <h4 className="font-semibold text-gray-900">{t('settings.billingIdentity')}</h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <Input
                          label={t('settings.siretLabel')}
                          inputMode="numeric"
                          autoComplete="off"
                          placeholder={t('settings.siretPlaceholder')}
                          {...register('siret', {
                            validate: (value) =>
                              isValidSiret(value || '') || t('settings.siretInvalid'),
                            onChange: (e) => {
                              const formatted = formatSiretDisplay(e.target.value);
                              setValue('siret', formatted, { shouldDirty: true, shouldValidate: true });
                            },
                          })}
                          error={errors.siret?.message}
                        />
                        {derivedSiren ? (
                          <p className="mt-1 text-xs text-gray-600">
                            {t('settings.sirenDerived')}:{' '}
                            <span className="font-medium tabular-nums tracking-wide">{derivedSiren}</span>
                          </p>
                        ) : null}
                      </div>

                      <Input
                        label={t('settings.vatNumberLabel')}
                        autoComplete="off"
                        placeholder={t('settings.vatNumberPlaceholder')}
                        {...register('vatNumber', {
                          onChange: (e) => {
                            setValue('vatNumber', normalizeVatNumber(e.target.value), {
                              shouldDirty: true,
                            });
                          },
                        })}
                        error={errors.vatNumber?.message}
                      />

                      <Input
                        label={t('settings.addressLine1Label')}
                        autoComplete="street-address"
                        placeholder={t('settings.addressLine1Placeholder')}
                        {...register('addressLine1')}
                        error={errors.addressLine1?.message}
                      />
                      <Input
                        label={t('settings.addressLine2Label')}
                        autoComplete="off"
                        placeholder={t('settings.addressLine2Placeholder')}
                        {...register('addressLine2')}
                        error={errors.addressLine2?.message}
                      />
                      <Input
                        label={t('settings.postalCodeLabel')}
                        autoComplete="postal-code"
                        placeholder={t('settings.postalCodePlaceholder')}
                        {...register('postalCode')}
                        error={errors.postalCode?.message}
                      />
                      <Input
                        label={t('settings.cityLabel')}
                        autoComplete="address-level2"
                        placeholder={t('settings.cityPlaceholder')}
                        {...register('city')}
                        error={errors.city?.message}
                      />
                      <Input
                        label={t('settings.countryCodeLabel')}
                        autoComplete="country"
                        placeholder="FR"
                        maxLength={2}
                        {...register('countryCode', {
                          validate: (value) => {
                            const code = normalizeCountryCode(value || '');
                            if (!code) return true;
                            return code.length === 2 || t('settings.countryCodeInvalid');
                          },
                          onChange: (e) => {
                            setValue('countryCode', normalizeCountryCode(e.target.value) || '', {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          },
                        })}
                        error={errors.countryCode?.message}
                      />
                    </div>
                  </div>

                  <div className="space-y-4 border-t border-gray-100 pt-6">
                    <h4 className="font-semibold text-gray-900">{t('settings.financial')}</h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <Input
                        id="settings-tax-rate"
                        label={t('settings.taxRateLabel')}
                        type="number"
                        step="0.1"
                        {...register('taxRate', {
                          required: t('settings.taxRateRequired'),
                          min: { value: 0, message: t('settings.positiveRate') },
                          max: { value: 100, message: t('settings.maxRate') },
                        })}
                        highlighted={highlightTaxRate}
                        error={errors.taxRate?.message}
                        placeholder="25"
                      />
                    </div>

                    <div className={`p-5 ${alertBox.info.container}`}>
                      <h4 className={`mb-3 ${alertBox.info.title}`}>
                        {t('settings.information')}
                      </h4>
                      <p className={`text-sm mb-3 ${alertBox.info.text}`}>
                        {t('settings.taxRateHint')}
                      </p>
                      <ul className={`text-sm space-y-2 ${alertBox.info.text}`}>
                        <li className="flex items-start gap-2">
                          <span className={`${alertBox.info.icon} mt-0.5`}>•</span>
                          <a
                            href="https://entreprendre.service-public.gouv.fr/vosdroits/F23267"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2 hover:opacity-80"
                          >
                            {t('settings.microEnterprise')}
                          </a>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className={`${alertBox.info.icon} mt-0.5`}>•</span>
                          <a
                            href="https://entreprendre.service-public.gouv.fr/vosdroits/F23575"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2 hover:opacity-80"
                          >
                            {t('settings.eurlSasu')}
                          </a>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className={`${alertBox.info.icon} mt-0.5`}>•</span>
                          <span>{t('settings.consultAccountant')}</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex justify-end border-t border-gray-100 pt-6">
                    <Button
                      type="submit"
                      loading={isLoading}
                      icon={<Save className="w-4 h-4" />}
                    >
                      {t('common.save')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {activeTab === 'preferences' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.preferences')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={PAGE_STACK}>
                  <section className="space-y-4">
                    <h4 className="font-semibold text-gray-900">{t('settings.language')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={async () => {
                          if (language === 'en') return;
                          await setLanguage('en');
                        }}
                        className={`p-5 border-2 rounded-xl text-left transition-all duration-200 ${
                          language === 'en'
                            ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center">
                          <US className="w-8 h-6 mr-4 rounded-sm" />
                          <h5 className="font-semibold text-gray-900">English</h5>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={async () => {
                          if (language === 'fr') return;
                          await setLanguage('fr');
                        }}
                        className={`p-5 border-2 rounded-xl text-left transition-all duration-200 ${
                          language === 'fr'
                            ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center">
                          <FR className="w-8 h-6 mr-4 rounded-sm" />
                          <h5 className="font-semibold text-gray-900">Français</h5>
                        </div>
                      </button>
                    </div>
                  </section>

                  <section className="space-y-4 border-t border-gray-100 pt-6">
                    <h4 className="font-semibold text-gray-900">{t('settings.appColor')}</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {presets.map((preset) => {
                        const selected = accentId === preset.id;
                        return (
                          <button
                            key={preset.id}
                            type="button"
                            onClick={() => handleAccentChange(preset.id)}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                              selected
                                ? 'border-primary-500 bg-primary-50/80 shadow-sm'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            <span
                              className="relative w-9 h-9 rounded-full shrink-0 shadow-inner ring-2 ring-white"
                              style={{ backgroundColor: preset.swatch }}
                              aria-hidden
                            >
                              {selected && (
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <Check className="w-4 h-4 text-white drop-shadow" strokeWidth={3} />
                                </span>
                              )}
                            </span>
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {t(preset.labelKey)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="space-y-3 border-t border-gray-100 pt-6">
                    <h4 className="font-semibold text-gray-900">{t('settings.eventTypeColors')}</h4>
                    <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden bg-white">
                      {CALENDAR_EVENT_TYPE_IDS.map((typeId) => (
                        <div
                          key={typeId}
                          className="flex items-center justify-between gap-3 px-3.5 py-2.5"
                        >
                          <span className="text-sm text-gray-800 truncate min-w-0">
                            {t(CALENDAR_EVENT_TYPE_I18N_KEYS[typeId])}
                          </span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {CALENDAR_COLOR_PALETTE.map(({ value, swatch, labelKey }) => {
                              const selected = eventTypeColors[typeId] === value;
                              return (
                                <button
                                  key={`${typeId}-${value}`}
                                  type="button"
                                  onClick={() => setEventTypeColor(typeId, value)}
                                  className={`relative w-6 h-6 rounded-full transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 ${
                                    selected
                                      ? 'ring-2 ring-slate-700 ring-offset-1'
                                      : 'ring-1 ring-black/10 hover:ring-black/20'
                                  }`}
                                  style={{ backgroundColor: swatch }}
                                  aria-label={`${t(CALENDAR_EVENT_TYPE_I18N_KEYS[typeId])} — ${t(labelKey)}`}
                                  aria-pressed={selected}
                                >
                                  {selected && (
                                    <Check
                                      className="absolute inset-0 m-auto w-3 h-3 text-white drop-shadow"
                                      strokeWidth={3}
                                    />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="space-y-3 border-t border-gray-100 pt-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900">{t('settings.followUpReminder')}</h4>
                        <p className="text-sm text-gray-600 mt-1">{t('settings.followUpReminderDescription')}</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={followUpReminderOnDueDay}
                        aria-label={t('settings.followUpReminder')}
                        disabled={!followUpReminderReady}
                        onClick={() => {
                          if (!followUpReminderReady || !authUser) return;
                          const next = !followUpReminderOnDueDay;
                          setFollowUpReminderOnDueDay(next);
                          void (async () => {
                            const updated = await profilesService.mergePreferences({
                              followUpReminderOnDueDay: next,
                            });
                            if (!updated) {
                              setFollowUpReminderOnDueDay(!next);
                              showToast(t('settings.followUpReminderSaveError'), 'error');
                              return;
                            }
                            setProfileData(updated);
                          })();
                        }}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                          followUpReminderOnDueDay ? 'bg-primary-500' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          aria-hidden
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                            followUpReminderOnDueDay ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </section>

                  {gmailStatus?.connected ? (
                    <section id="settings-email-signature" className="space-y-3 border-t border-gray-100 pt-6">
                      <h4 className="font-semibold text-gray-900">{t('settings.emailSignature')}</h4>
                      <p className="text-sm text-gray-600">{t('settings.emailSignatureDescription')}</p>
                      <EmailSignatureEditor
                        html={emailSignatureHtml}
                        plain={emailSignature}
                        highlighted={highlightEmailSignature}
                        highlightClassName={highlightRing.combined}
                        onChange={({ html, plain }) => {
                          setEmailSignatureHtml(html);
                          setEmailSignature(plain);
                        }}
                        onTooLarge={(kind) => {
                          showToast(
                            kind === 'html'
                              ? t('settings.emailSignatureTooLargeHtml', {
                                  max: EMAIL_SIGNATURE_MAX_HTML_CHARS.toLocaleString(),
                                })
                              : t('settings.emailSignatureTooLargePlain', {
                                  max: EMAIL_SIGNATURE_MAX_PLAIN_CHARS.toLocaleString(),
                                }),
                            'error'
                          );
                        }}
                      />
                    </section>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'integrations' && (
            <Card>
              <CardContent>
                <div className={PAGE_STACK}>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="min-w-0 text-lg font-semibold text-gray-900">
                        {t('settings.integrations')}
                      </h3>
                      <span className="inline-flex shrink-0 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 ring-1 ring-primary-200/70">
                        {t('settings.integrationsConnectedCount', {
                          connected: integrationsConnectedCount,
                          total: ENABLE_SUPER_PDP ? 4 : 3,
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{t('settings.integrationsSubtitle')}</p>
                  </div>

                  <div className={`grid grid-cols-1 xl:grid-cols-2 ${PAGE_GRID_GAP}`}>
                    <IntegrationCard
                      title={t('settings.googleCalendar')}
                      description={t('settings.googleCalendarDescription')}
                      icon={
                        <img
                          src="/images/brands/google-calendar.svg"
                          alt=""
                          className="h-8 w-8"
                          aria-hidden
                        />
                      }
                      iconVariant="brand"
                      status={gcalCardStatus}
                      email={gcalStatus?.email}
                      syncHint={
                        gcalCardStatus === 'connected' && gcalBusy
                          ? t('settings.googleCalendarSyncing')
                          : null
                      }
                      connectLabel={t('settings.integrationConnect')}
                      connectingLabel={t('settings.googleCalendarConnecting')}
                      disconnectLabel={t('settings.integrationDisconnect')}
                      unavailableMessage={t('settings.googleCalendarNotConfigured')}
                      errorMessage={t('settings.googleCalendarLoadError')}
                      busy={gcalBusy}
                      onConnect={handleGcalConnect}
                      onDisconnect={handleGcalDisconnect}
                      onRetry={() => {
                        setGcalLoading(true);
                        setGcalLoadError(false);
                        googleCalendarService
                          .getStatus()
                          .then(setGcalStatus)
                          .catch(() => setGcalLoadError(true))
                          .finally(() => setGcalLoading(false));
                      }}
                    />

                    <IntegrationCard
                      title={t('settings.googleGmail')}
                      description={t('settings.googleGmailDescription')}
                      icon={
                        <img
                          src="/images/brands/gmail.svg"
                          alt=""
                          className="h-8 w-8"
                          aria-hidden
                        />
                      }
                      iconVariant="brand"
                      status={gmailCardStatus}
                      email={gmailStatus?.email}
                      connectLabel={t('settings.integrationConnect')}
                      connectingLabel={t('settings.googleGmailConnecting')}
                      disconnectLabel={t('settings.integrationDisconnect')}
                      unavailableMessage={t('settings.googleGmailNotConfigured')}
                      busy={gmailBusy}
                      onConnect={handleGmailConnect}
                      onDisconnect={handleGmailDisconnect}
                    />

                    <IntegrationCard
                      title={t('settings.googleDrive')}
                      description={t('settings.googleDriveDescription')}
                      icon={
                        <img
                          src="/images/brands/google-drive.svg"
                          alt=""
                          className="h-8 w-8"
                          aria-hidden
                        />
                      }
                      iconVariant="brand"
                      status={gdriveCardStatus}
                      email={gdriveStatus?.email}
                      connectLabel={t('settings.integrationConnect')}
                      connectingLabel={t('settings.googleDriveConnecting')}
                      disconnectLabel={t('settings.integrationDisconnect')}
                      unavailableMessage={t('settings.googleDriveNotConfigured')}
                      busy={gdriveBusy}
                      onConnect={handleGdriveConnect}
                      onDisconnect={handleGdriveDisconnect}
                    />

                    {ENABLE_SUPER_PDP ? (
                      <IntegrationCard
                        title={t('settings.superPdp')}
                        description={t('settings.superPdpDescription')}
                        icon={<FileText className="h-7 w-7" />}
                        status="disconnected"
                        connectLabel={t('settings.integrationConnect')}
                        disconnectLabel={t('settings.integrationDisconnect')}
                        onConnect={handleSuperPdpConnect}
                      />
                    ) : null}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeTab === 'subscription' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.subscription')}</CardTitle>
              </CardHeader>
              <CardContent>
                {usageLoading ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">{t('common.loading')}</p>
                  </div>
                ) : usage && usage.plan ? (
                  <div className={PAGE_STACK}>
                    {/* Plan Info */}
                    <div className="p-5 bg-primary-50/80 rounded-xl border border-primary-200/60">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex-1">
                          <h4 className="font-semibold text-primary-900 mb-1">
                            {t('settings.currentPlan')}: {usage.plan.name === 'free' ? t('settings.planFree') : t('settings.planPro')}
                          </h4>
                          <p className="text-sm text-primary-700">
                            {usage.plan.name === 'free' ? t('settings.planFreeDescription') : t('settings.planProDescription')}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 sm:flex-shrink-0">
                          <div className="px-3 py-1.5 bg-primary-100 rounded-full shadow-sm">
                            <span className="text-sm font-semibold text-primary-800 uppercase tracking-wide">
                              {usage.plan.name}
                            </span>
                          </div>
                          {usage.plan.name === 'free' ? (
                            <Button
                              onClick={() => {
                                navigateToPricing(navigate);
                              }}
                              variant="primary"
                              size="sm"
                              icon={<Sparkles className="w-4 h-4" />}
                            >
                              {t('nav.upgradeToPro')}
                            </Button>
                          ) : usage.plan.name === 'pro' ? (
                            <Button
                              onClick={handleManageSubscription}
                              variant="primary"
                              size="sm"
                              icon={<SettingsIcon className="w-4 h-4" />}
                              disabled={managingSubscription}
                            >
                              {managingSubscription ? t('common.loading') : t('settings.manageSubscription')}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {/* Usage List */}
                    <div className="space-y-4">
                      {/* Leads */}
                      <UsageBar
                        label={t('settings.usageLeads')}
                        current={usage.current.leads}
                        limit={usage.limits.leads}
                      />

                      {/* Projects */}
                      <UsageBar
                        label={t('settings.usageProjects')}
                        current={usage.current.projects}
                        limit={usage.limits.projects}
                      />

                      {/* Payments */}
                      <UsageBar
                        label={t('settings.usagePayments')}
                        current={usage.current.payments}
                        limit={usage.limits.payments}
                      />

                      {/* Message Templates */}
                      <UsageBar
                        label={t('settings.usageEmailTemplates')}
                        current={usage.current.message_templates}
                        limit={usage.limits.message_templates}
                      />

                      {/* Storage */}
                      <UsageBar
                        label={t('settings.usageStorage')}
                        current={usage.current.storage_bytes}
                        limit={usage.limits.storage_bytes}
                        storageFromBytes
                      />

                      {/* AI rewrite — Pro feature */}
                      <div
                        className={`flex items-center gap-3.5 rounded-xl border p-4 ${
                          usage.plan.has_ai_rewrite
                            ? 'border-primary-200/60 bg-primary-50/40'
                            : 'border-gray-200/80 bg-white'
                        }`}
                      >
                        <span
                          className={`inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
                            usage.plan.has_ai_rewrite
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          <Sparkles className="h-4 w-4" aria-hidden />
                        </span>
                        <div className="min-w-0 flex-1">
                          <h5 className="font-medium text-gray-900">
                            {t('pricing.featureAIRewrite')}
                          </h5>
                        </div>
                        {usage.plan.has_ai_rewrite ? (
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-primary-700">
                            <Check className="h-4 w-4" aria-hidden />
                            {t('settings.featureIncluded')}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">
                            {t('settings.featureNotIncluded')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600">{t('settings.usageError')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === 'account' && (
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.account')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={PAGE_STACK}>
                  {/* Data Export */}
                  <div className="p-5 rounded-xl border border-gray-200/60 bg-gray-50/50">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('settings.dataExport')}</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        {t('settings.dataExportDescription')}
                      </p>
                      <Button
                        variant="primary"
                        size="md"
                        onClick={handleExportAllData}
                        icon={<Download className="w-4 h-4" />}
                        loading={isExportingData}
                        disabled={isExportingData || !authUser || (exportCooldownSeconds !== null && exportCooldownSeconds > 0)}
                        className="w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow"
                      >
                        {isExportingData
                          ? (t('settings.exportingData') || 'Export en cours...')
                          : exportCooldownSeconds !== null && exportCooldownSeconds > 0
                          ? (t('settings.exportCooldownRemaining', {
                              minutes: Math.floor(exportCooldownSeconds / 60),
                              seconds: exportCooldownSeconds % 60,
                            }) || `Réessayer dans ${Math.floor(exportCooldownSeconds / 60)}min ${exportCooldownSeconds % 60}s`)
                          : (t('settings.downloadAllData') || 'Télécharger toutes mes données')}
                      </Button>
                    </div>
                  </div>

                  {/* Danger zone — delete account */}
                  <div className="p-5 rounded-xl border border-red-200/80 bg-red-50/50">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">{t('settings.deleteAccount')}</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        {t('settings.deleteAccountDescription')}
                      </p>
                      <Button
                        variant="danger"
                        size="md"
                        onClick={() => setIsDeleteAccountOpen(true)}
                        icon={<Trash2 className="w-4 h-4" />}
                        disabled={!authUser}
                        className="w-full sm:w-auto"
                      >
                        {t('settings.deleteAccountButton')}
                      </Button>
                    </div>
                  </div>

                  {/* PWA */}
                  <div className="space-y-4 border-t border-gray-100 pt-6">
                    <div className="p-5 rounded-xl border border-gray-200/60 bg-gray-50/50">
                      <h4 className="font-semibold text-gray-900 mb-2">{t('pwa.installTitle')}</h4>
                      <p className="text-gray-600 text-sm mb-4">{t('pwa.installDescription')}</p>
                      <div>
                        <InstallPWAButton variant="primary" size="md" />
                      </div>
                    </div>

                    <div className="p-5 rounded-xl border border-gray-200/60 bg-gray-50/50">
                      <h4 className="font-semibold text-gray-900 mb-3">{t('pwa.manualInstallTitle')}</h4>
                      <div className="space-y-3 text-sm text-gray-700">
                        <div className="flex items-start gap-2">
                          <strong className="text-gray-900 font-semibold">Chrome / Edge :</strong>
                          <span>{t('pwa.manualInstallChrome')}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <strong className="text-gray-900 font-semibold">Safari (iOS) :</strong>
                          <span>{t('pwa.manualInstallSafari')}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <strong className="text-gray-900 font-semibold">Firefox :</strong>
                          <span>{t('pwa.manualInstallFirefox')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </div>

      <DeleteAccountModal
        isOpen={isDeleteAccountOpen}
        onClose={() => setIsDeleteAccountOpen(false)}
        onDeleted={async () => {
          setIsDeleteAccountOpen(false);
          showToast(t('settings.deleteAccountSuccess'), 'success');
          await signOut();
        }}
      />

      {ENABLE_SUPER_PDP ? (
        <UpgradePromptModal
          isOpen={showSuperPdpProModal}
          onClose={() => setShowSuperPdpProModal(false)}
          title={t('settings.superPdpProFeatureTitle')}
          message={t('settings.superPdpProFeatureMessage')}
          onUpgrade={() => {
            setShowSuperPdpProModal(false);
            navigateToPricing(navigate);
          }}
        />
      ) : null}
    </div>
  );
};

export default Settings;
